import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";

import api from "../../../services/api";
import PrevisaoSaidaTecido from "./PrevisaoSaidaTecido";

const API_ROUTE = "relatorios-novos/saida-tecido";

const createDefaultFilters = () => ({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  mes_ano: { value: null, matchMode: FilterMatchMode.CONTAINS },
  artigo: { value: null, matchMode: FilterMatchMode.CONTAINS },
  tipo: { value: null, matchMode: FilterMatchMode.CONTAINS },
  soma: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const outputTypeOptions = [
  { label: "Todas as saídas", value: "todos" },
  { label: "Tecido venda", value: "V" },
  { label: "Tecido consumo", value: "C" },
];

const outputTypeLabels = {
  C: "Consumo",
  V: "Venda",
};

const metersFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatMeters = (value) =>
  `${metersFormatter.format(Number(value || 0))} m`;

const chartWrapperStyle = {
  height: "32rem",
  width: "100%",
};

const emptyChartStyle = {
  height: "32rem",
};

const groupItemsByArticle = (items) =>
  Object.values(
    items.reduce((accumulator, item) => {
      const key = item.artigo;

      if (!accumulator[key]) {
        accumulator[key] = {
          artigo: item.artigo,
          soma: 0,
        };
      }

      accumulator[key].soma += Number(item.soma || 0);

      return accumulator;
    }, {}),
  ).sort((firstItem, secondItem) =>
    String(firstItem.artigo).localeCompare(String(secondItem.artigo), "pt-BR", {
      numeric: true,
    }),
  );

const normalizeReportData = (data) => {
  const responseData = data?.data || data;
  const porMesArtigo = Array.isArray(responseData?.por_mes_artigo)
    ? responseData.por_mes_artigo
    : [];
  const totalPorArtigo = Array.isArray(responseData?.total_por_artigo)
    ? responseData.total_por_artigo
    : groupItemsByArticle(porMesArtigo);

  return {
    geralMes: Array.isArray(responseData?.geral_mes)
      ? responseData.geral_mes
      : [],
    porMesArtigo,
    producaoTecelagem: Array.isArray(responseData?.producao_tecelagem)
      ? responseData.producao_tecelagem
      : [],
    totalPorArtigo,
  };
};

const formatDateParam = (date) => {
  if (!date) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getArticleOptions = (items) =>
  Array.from(new Set(items.map((item) => item.artigo).filter(Boolean)))
    .sort((firstArticle, secondArticle) =>
      String(firstArticle).localeCompare(String(secondArticle), "pt-BR", {
        numeric: true,
      }),
    )
    .map((artigo) => ({
      label: String(artigo),
      value: artigo,
    }));

const groupItemsByMonth = (items) =>
  Object.values(
    items.reduce((accumulator, item) => {
      const key = item.mes_ano;

      if (!accumulator[key]) {
        accumulator[key] = {
          mes_ano: item.mes_ano,
          mes_ref: item.mes_ref,
          soma: 0,
        };
      }

      accumulator[key].soma += Number(item.soma || 0);

      return accumulator;
    }, {}),
  ).sort(
    (firstItem, secondItem) =>
      new Date(firstItem.mes_ref) - new Date(secondItem.mes_ref),
  );

const groupProductionByMonth = (items) =>
  Object.values(
    items.reduce((accumulator, item) => {
      const key = item.mes_ano;

      if (!accumulator[key]) {
        accumulator[key] = {
          mes_ano: item.mes_ano,
          metragem: 0,
        };
      }

      accumulator[key].metragem += Number(item.metragem || 0);

      return accumulator;
    }, {}),
  );

const sortMonthYear = (firstMonth, secondMonth) => {
  const [firstMonthNumber, firstYear] = String(firstMonth).split("/");
  const [secondMonthNumber, secondYear] = String(secondMonth).split("/");

  return (
    Number(firstYear) - Number(secondYear) ||
    Number(firstMonthNumber) - Number(secondMonthNumber)
  );
};

const createInitialStartDate = () => {
  const initialDate = new Date();
  initialDate.setFullYear(initialDate.getFullYear() - 3);

  return initialDate;
};

const createInitialEndDate = () => new Date();

const GraficoSaidaTecido = ({
  title = "Saída de tecido",
  showError,
  showSuccess,
}) => {
  const [startDate, setStartDate] = useState(createInitialStartDate);
  const [endDate, setEndDate] = useState(createInitialEndDate);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(createDefaultFilters);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [articleOptions, setArticleOptions] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [outputType, setOutputType] = useState("todos");
  const [compareProduction, setCompareProduction] = useState(false);
  const [reportData, setReportData] = useState({
    geralMes: [],
    porMesArtigo: [],
    producaoTecelagem: [],
    totalPorArtigo: [],
  });

  const outputDataByType = useMemo(() => {
    if (outputType === "todos") {
      return reportData.porMesArtigo;
    }

    return reportData.porMesArtigo.filter((item) => item.tipo === outputType);
  }, [outputType, reportData.porMesArtigo]);

  const visibleArticleData = useMemo(() => {
    if (!selectedArticles.length) {
      return outputDataByType;
    }

    return outputDataByType.filter((item) =>
      selectedArticles.includes(item.artigo),
    );
  }, [outputDataByType, selectedArticles]);

  const visibleMonthData = useMemo(
    () => groupItemsByMonth(visibleArticleData),
    [visibleArticleData],
  );

  const visibleTotalArticleData = useMemo(
    () => groupItemsByArticle(visibleArticleData),
    [visibleArticleData],
  );

  const canCompareProduction = selectedArticles.length > 0;
  const showProductionComparison = compareProduction && canCompareProduction;

  const visibleProductionData = useMemo(() => {
    if (!showProductionComparison) {
      return [];
    }

    const filteredProduction = reportData.producaoTecelagem.filter((item) =>
      selectedArticles.includes(item.artigo),
    );

    return groupProductionByMonth(filteredProduction);
  }, [
    reportData.producaoTecelagem,
    selectedArticles,
    showProductionComparison,
  ]);

  const chartMonthLabels = useMemo(
    () =>
      Array.from(
        new Set([
          ...visibleMonthData.map((item) => item.mes_ano),
          ...visibleProductionData.map((item) => item.mes_ano),
        ]),
      ).sort(sortMonthYear),
    [visibleMonthData, visibleProductionData],
  );

  const chartData = useMemo(() => {
    const outputByMonth = new Map(
      visibleMonthData.map((item) => [item.mes_ano, Number(item.soma || 0)]),
    );
    const productionByMonth = new Map(
      visibleProductionData.map((item) => [
        item.mes_ano,
        Number(item.metragem || 0),
      ]),
    );
    const datasets = [
      {
        label: "Saída de tecido",
        data: chartMonthLabels.map((month) => outputByMonth.get(month) || 0),
        borderColor: "#2563eb",
        backgroundColor: "rgba(37, 99, 235, 0.16)",
        tension: 0.35,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        datalabels: {
          align: "top",
          anchor: "end",
          offset: 6,
        },
      },
    ];

    if (showProductionComparison) {
      datasets.push({
        label: "Produção tecelagem",
        data: chartMonthLabels.map(
          (month) => productionByMonth.get(month) || 0,
        ),
        borderColor: "#ea580c",
        backgroundColor: "rgba(234, 88, 12, 0.16)",
        tension: 0.35,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        datalabels: {
          align: "bottom",
          anchor: "end",
          offset: 6,
        },
      });
    }

    return {
      labels: chartMonthLabels,
      datasets,
    };
  }, [
    chartMonthLabels,
    showProductionComparison,
    visibleMonthData,
    visibleProductionData,
  ]);

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: "#334155",
          },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatMeters(context.parsed.y)}`,
          },
        },
        datalabels: {
          color: "#334155",
          display: "auto",
          font: {
            weight: "bold",
          },
          formatter: (value) => formatMeters(value),
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#475569",
          },
          grid: {
            color: "#e2e8f0",
          },
        },
        y: {
          ticks: {
            color: "#475569",
            callback: (value) => formatMeters(value),
          },
          grid: {
            color: "#e2e8f0",
          },
        },
      },
    }),
    [],
  );

  const notifyError = useCallback(
    (message) => {
      if (typeof showError === "function") {
        showError(message);
      }
    },
    [showError],
  );

  const notifySuccess = useCallback(
    (message) => {
      if (typeof showSuccess === "function") {
        showSuccess(message);
      }
    },
    [showSuccess],
  );

  const handleGlobalFilterChange = (event) => {
    const value = event.target.value;

    setFilters((currentFilters) => ({
      ...currentFilters,
      global: {
        ...currentFilters.global,
        value,
      },
    }));
    setGlobalFilterValue(value);
  };

  const fetchData = useCallback(
    (showValidation = false) => {
      if (!startDate || !endDate) {
        if (showValidation) {
          notifyError("Informe a data inicial e a data final.");
        }

        return;
      }

      setLoading(true);

      api
        .get(API_ROUTE, {
          params: {
            data_inicial: formatDateParam(startDate),
            data_final: formatDateParam(endDate),
          },
        })
        .then((response) => {
          const nextReportData = normalizeReportData(response.data);
          const nextArticleOptions = getArticleOptions(
            nextReportData.porMesArtigo,
          );
          const nextSelectedArticles = nextArticleOptions.map(
            (item) => item.value,
          );

          setReportData(nextReportData);
          setArticleOptions(nextArticleOptions);
          setSelectedArticles(nextSelectedArticles);
          setCompareProduction(false);
          notifySuccess("Relatório carregado com sucesso.");
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            "Não foi possível carregar o relatório.";

          notifyError(message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [endDate, notifyError, notifySuccess, startDate],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const clearFilters = () => {
    setFilters(createDefaultFilters());
    setGlobalFilterValue("");
  };

  const metersBodyTemplate = (rowData) => formatMeters(rowData.soma);
  const outputTypeBodyTemplate = (rowData) =>
    outputTypeLabels[rowData.tipo] || rowData.tipo;

  const hasChartData = chartMonthLabels.length > 0;

  return (
    <section className="p-fluid">
      <div className="p-d-flex p-ai-start p-jc-between p-mb-4">
        <div>
          <span className="p-d-block p-mt-1">Período por saída faturada</span>
        </div>
      </div>

      <div className="p-formgrid p-grid p-ai-end p-mb-4">
        <div className="p-field p-col-12 p-md-2">
          <label>Data inicial</label>
          <Calendar
            value={startDate}
            onChange={(event) => setStartDate(event.value)}
            dateFormat="dd/mm/yy"
            showIcon
          />
        </div>

        <div className="p-field p-col-12 p-md-2">
          <label>Data final</label>
          <Calendar
            value={endDate}
            onChange={(event) => setEndDate(event.value)}
            dateFormat="dd/mm/yy"
            showIcon
          />
        </div>

        <div className="p-field p-col-12 p-md-2">
          <label>Tipo de saída</label>
          <Dropdown
            value={outputType}
            options={outputTypeOptions}
            onChange={(event) => setOutputType(event.value)}
            optionLabel="label"
            optionValue="value"
          />
        </div>

        <div className="p-field p-col-12 p-md-4">
          <label>Artigos</label>
          <MultiSelect
            filter
            value={selectedArticles}
            options={articleOptions}
            onChange={(event) => {
              setSelectedArticles(event.value);
              setCompareProduction(false);
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione os artigos"
            display="chip"
            disabled={!articleOptions.length}
          />
        </div>

        <div className="p-field p-col-12 p-md-2">
          <Button
            type="button"
            icon="pi pi-search"
            label={loading ? "Carregando" : "Buscar"}
            onClick={() => fetchData(true)}
            disabled={loading}
          />
        </div>
      </div>

      <div className="p-d-flex p-jc-end p-mb-3">
        <Button
          type="button"
          icon="pi pi-chart-line"
          label={
            compareProduction ? "Ocultar comparativo" : "Comparar com produção"
          }
          className="p-button-outlined"
          onClick={() => setCompareProduction((currentValue) => !currentValue)}
          disabled={!canCompareProduction}
        />
      </div>

      <div className="p-mb-4" style={chartWrapperStyle}>
        {hasChartData ? (
          <Chart
            type="line"
            data={chartData}
            options={chartOptions}
            plugins={[ChartDataLabels]}
            style={chartWrapperStyle}
          />
        ) : (
          <div
            className="p-d-flex p-ai-center p-jc-center p-text-center p-p-3"
            style={emptyChartStyle}
          >
            Nenhum dado para o gráfico.
          </div>
        )}
      </div>

      <h3 className="p-m-0 p-mb-3">Total por artigo</h3>

      <DataTable
        value={visibleTotalArticleData}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        responsiveLayout="scroll"
        emptyMessage="Nenhum registro encontrado."
        className="p-mb-4"
      >
        <Column field="artigo" header="Artigo" sortable />
        <Column
          field="soma"
          header="Metros"
          sortable
          dataType="numeric"
          body={metersBodyTemplate}
        />
      </DataTable>

      <div className="p-d-flex p-flex-wrap p-ai-center p-jc-between p-mb-3">
        <h3 className="p-m-0">Saída por mês e artigo</h3>

        <div className="p-d-flex p-ai-center p-flex-wrap">
          <span className="p-input-icon-left p-mr-2">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={globalFilterValue}
              onChange={handleGlobalFilterChange}
              placeholder="Filtrar"
            />
          </span>

          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Limpar"
            className="p-button-outlined"
            onClick={clearFilters}
          />
        </div>
      </div>

      <DataTable
        value={visibleArticleData}
        filters={filters}
        onFilter={(event) => setFilters(event.filters)}
        globalFilterFields={["mes_ano", "artigo", "tipo", "soma"]}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        responsiveLayout="scroll"
        emptyMessage="Nenhum registro encontrado."
      >
        <Column
          field="mes_ano"
          header="Mês/Ano"
          sortable
          filter
          filterPlaceholder="Filtrar mês"
        />
        <Column
          field="artigo"
          header="Artigo"
          sortable
          filter
          filterPlaceholder="Filtrar artigo"
        />
        <Column
          field="tipo"
          header="Tipo"
          sortable
          filter
          filterPlaceholder="Filtrar tipo"
          body={outputTypeBodyTemplate}
        />
        <Column
          field="soma"
          header="Metros"
          sortable
          filter
          dataType="numeric"
          body={metersBodyTemplate}
        />
      </DataTable>

      <PrevisaoSaidaTecido
        articleOptions={articleOptions.filter((option) =>
          selectedArticles.includes(option.value),
        )}
        endDate={endDate}
        items={visibleArticleData}
        startDate={startDate}
      />
    </section>
  );
};

export default GraficoSaidaTecido;
